import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Input,
  Label,
  FormGroup,
  Progress,
  Alert,
} from "reactstrap";
import CommonCardHeader from "../../Utils/CommonComponents/CommonCardHeader";
import http from "../../services/http";
import mascotImages from "../../mascot-images.json";

const VideoGenerator = () => {
  const [selectedMascot, setSelectedMascot] = useState(null);
  const [prompt, setPrompt] = useState("slow motion, enjoying, playing");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [videoJobIds, setVideoJobIds] = useState([]);

  const API_BASE = process.env.REACT_APP_VIDEO_API;

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const pollImageStatus = async (imageJobIds) => {
    while (true) {
      try {
        const response = await http.post(`${API_BASE}/check-image-status`, {
          imageJobIds,
        });
        const { image_statuses } = response.data;

        const completed = image_statuses.filter(
          (img) => img.status === "complete"
        );
        const failed = image_statuses.filter((img) => img.status === "failed");

        setProgress(50 + (completed.length / imageJobIds.length) * 25);
        setStatus(`Images: ${completed.length}/${imageJobIds.length} complete`);

        if (failed.length > 0) {
          throw new Error(`${failed.length} images failed to generate`);
        }

        if (completed.length === imageJobIds.length) {
          return completed;
        }

        await sleep(30000);
      } catch (err) {
        throw new Error(`Image polling failed: ${err.message}`);
      }
    }
  };

  const pollVideoStatus = async (videoJobIds) => {
    while (true) {
      try {
        const response = await http.post(`${API_BASE}/check-video-status`, {
          job_ids: videoJobIds,
        });
        const { job_statuses } = response.data;

        const completed = job_statuses.filter(
          (video) => video.status === "COMPLETE"
        );
        const failed = job_statuses.filter(
          (video) => video.status === "FAILED"
        );

        setProgress(75 + (completed.length / videoJobIds.length) * 25);
        setStatus(`Videos: ${completed.length}/${videoJobIds.length} complete`);

        if (failed.length > 0) {
          throw new Error(`${failed.length} videos failed to generate`);
        }

        if (completed.length === videoJobIds.length) {
          return completed;
        }

        await sleep(60000);
      } catch (err) {
        throw new Error(`Video polling failed: ${err.message}`);
      }
    }
  };

  const generateVideo = async () => {
    if (!selectedMascot) {
      setError("Please select a mascot first");
      return;
    }

    setLoading(true);
    setError("");
    setProgress(10);

    try {
      setStatus("Creating video...");
      const script = {
        scenes: [
          {
            scene_number: 1,
            prompt: prompt,
            image_id: selectedMascot.leonardo_id,
            duration: 8,
          },
        ],
      };

      const videosResponse = await http.post(
        `${API_BASE}/generate-leonardo-videos`,
        { script }
      );
      const jobIds = videosResponse.data.job_ids;
      setVideoJobIds(jobIds);
      setProgress(50);

      setStatus("Waiting for video to complete...");
      const completedVideos = await pollVideoStatus(jobIds);

      setGeneratedVideos(completedVideos);
      setProgress(100);
      setStatus("Video generation complete!");
    } catch (err) {
      setError(err.message);
      setStatus("Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const resetGenerator = () => {
    setProgress(0);
    setStatus("");
    setError("");
    setGeneratedVideos([]);
    setVideoJobIds([]);
    setSelectedMascot(null);
  };

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <Col sm={12}>
            <Card>
              <CommonCardHeader title="AI Video Generator" />
              <CardBody>
                {!loading && generatedVideos.length === 0 && (
                  <Row>
                    <Col md={8}>
                      <FormGroup>
                        <Label>Select Mascot</Label>
                        <Row>
                          {Object.entries(mascotImages).map(([name, data]) => {
                            const imageUrl = `https://cdn.leonardo.ai/users/dce9b58d-c70d-421b-94cd-a6ac41f35a15/initImages/${data.leonardo_id}.png`;
                            return (
                              <Col md={3} key={name} className="mb-3">
                                <div
                                  className={`border rounded p-2 cursor-pointer ${
                                    selectedMascot?.leonardo_id ===
                                    data.leonardo_id
                                      ? "border-primary bg-light"
                                      : ""
                                  }`}
                                  onClick={() => setSelectedMascot(data)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <img
                                    src={imageUrl}
                                    alt={name}
                                    className="img-fluid rounded mb-2"
                                    style={{
                                      height: "120px",
                                      objectFit: "cover",
                                      width: "100%",
                                    }}
                                  />
                                  <small className="d-block text-center">
                                    {name
                                      .replace(".jpg", "")
                                      .replace(".png", "")}
                                  </small>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </FormGroup>

                      <FormGroup>
                        <Label>Video Prompt</Label>
                        <Input
                          type="textarea"
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          placeholder="e.g., slow motion, fighting with bugs"
                          rows={3}
                        />
                      </FormGroup>

                      <Button
                        color="primary"
                        onClick={generateVideo}
                        disabled={loading || !selectedMascot}
                      >
                        {loading ? "Generating..." : "Generate Video"}
                      </Button>
                    </Col>

                    <Col md={4}>
                      {selectedMascot && (
                        <div className="p-3 bg-light rounded">
                          <h6>Selected Mascot:</h6>
                          <img
                            src={`https://cdn.leonardo.ai/users/dce9b58d-c70d-421b-94cd-a6ac41f35a15/initImages/${selectedMascot.leonardo_id}.png`}
                            alt="Selected"
                            className="img-fluid rounded mb-2"
                          />
                          <p className="small mb-0">Duration: 6-8 seconds</p>
                        </div>
                      )}
                    </Col>
                  </Row>
                )}

                {(loading || generatedVideos.length > 0) && (
                  <div>
                    <div className="mb-4">
                      <div className="d-flex justify-content-between mb-2">
                        <span>{status}</span>
                        <span>{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} />
                      <small className="text-muted mt-1">{status}</small>
                    </div>

                    {error && (
                      <Alert color="danger">
                        <strong>Error:</strong> {error}
                        <Button
                          color="link"
                          size="sm"
                          onClick={resetGenerator}
                          className="ms-2"
                        >
                          Try Again
                        </Button>
                      </Alert>
                    )}

                    {generatedVideos.length > 0 && (
                      <div>
                        <h5>Generated Videos</h5>
                        <Row>
                          {generatedVideos.map((video, index) => (
                            <Col md={6} key={video.jobId} className="mb-3">
                              <Card>
                                <CardBody>
                                  <h6>Scene {index + 1}</h6>
                                  <video
                                    controls
                                    width="100%"
                                    height="300"
                                    style={{ maxWidth: "300px" }}
                                  >
                                    <source
                                      src={video.videoUrl}
                                      type="video/mp4"
                                    />
                                  </video>
                                  <div className="mt-2">
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() => {
                                        const link =
                                          document.createElement("a");
                                        link.href = video.videoUrl;
                                        link.download = `mascot-video-${
                                          index + 1
                                        }.mp4`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                    >
                                      Download
                                    </Button>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          ))}
                        </Row>

                        <Button
                          color="primary"
                          onClick={resetGenerator}
                          className="mt-3"
                        >
                          Generate Another Video
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VideoGenerator;
